import { z } from 'zod';
import {
  MAX_SPEC_ITEMS,
  SpecItemSchema,
  SpecSectionSchema,
  POSITION_STEP,
  inSheetOrder,
  isSpecSectionEmpty,
  nextPosition,
  positionBetween,
  type SpecItem,
  type SpecSection,
} from '../../domain/entities/spec-section.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';

/**
 * A job's specification sheet (SCRUM-141, P8-05).
 *
 * What the sheet is *for* shapes every refusal below: it is the written
 * record of what the owner is buying, read on site and handed over at the
 * end. So it may be incomplete while it is being written, and it may not
 * be quietly rewritten after it has been shown.
 */
export class SpecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SpecError';
  }
}

export interface SpecContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

const scope = (context: SpecContext) => ({
  companyId: context.companyId,
  jobId: context.jobId,
});

const sections = (context: SpecContext) =>
  context.data.collection<SpecSection>(scope(context), 'specSections');

/**
 * The one query an owner is allowed to issue.
 *
 * Like `ownWarrantyClaimQuery`, the filter is the permission rather than an
 * optimisation: `allow list` is evaluated per returned document, so asking
 * for the whole sheet fails on the first unshared section and asking for
 * `shared == true` succeeds. A page cannot be trusted to filter, and here
 * it is not even allowed to try.
 */
export function sharedSpecSectionQuery(): Query {
  return {
    where: [{ field: 'shared', op: '==', value: true }],
    orderBy: [{ field: 'position', direction: 'asc' }],
  };
}

export async function listSpecSections(context: SpecContext): Promise<SpecSection[]> {
  const page = await sections(context).list();
  return inSheetOrder(page.items);
}

export async function listSharedSpecSections(context: SpecContext): Promise<SpecSection[]> {
  const page = await sections(context).list(sharedSpecSectionQuery());
  return inSheetOrder(page.items);
}

export function subscribeToSharedSpecSections(
  context: SpecContext,
  onChange: (sections: readonly SpecSection[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return sections(context).subscribe(
    sharedSpecSectionQuery(),
    (page) => onChange(inSheetOrder(page.items)),
    onError,
  );
}

export async function getSpecSection(context: SpecContext, id: string): Promise<SpecSection> {
  const found = await sections(context).get(id);
  if (!found) throw new NotFoundError('specSections', id);
  return found;
}

export const SectionInputSchema = z.object({
  title: z.string().trim().min(1, 'A section needs a title').max(200),
  notes: z.string().trim().max(20_000).default(''),
});
export type SectionInput = z.input<typeof SectionInputSchema>;

/**
 * Add a section.
 *
 * It arrives **unshared**. A sheet is written room by room over months, and
 * a section that published itself the moment it was created would show the
 * owner a heading with nothing under it.
 */
export async function createSpecSection(
  context: SpecContext,
  input: SectionInput,
): Promise<SpecSection> {
  const parsed = SectionInputSchema.safeParse(input);
  if (!parsed.success) throw new SpecError(parsed.error.issues[0]!.message);

  const existing = await listSpecSections(context);
  const at = context.clock.now().toISOString();
  const section = SpecSectionSchema.parse({
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    title: parsed.data.title,
    notes: parsed.data.notes,
    position: nextPosition(existing),
    shared: false,
    items: [],
    linkedRecords: [],
    linkedKeys: [],
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
    version: 1,
  });
  await sections(context).create(section);
  return section;
}

async function write(
  context: SpecContext,
  existing: SpecSection,
  patch: Partial<SpecSection>,
): Promise<SpecSection> {
  const updatedAt = context.clock.now().toISOString();
  await context.data.commit([
    updateWrite<SpecSection>(
      scope(context),
      'specSections',
      existing.id,
      { ...patch, updatedAt, updatedBy: context.uid },
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch, updatedAt, updatedBy: context.uid };
}

export async function editSpecSection(
  context: SpecContext,
  section: SpecSection,
  input: SectionInput,
): Promise<SpecSection> {
  const parsed = SectionInputSchema.safeParse(input);
  if (!parsed.success) throw new SpecError(parsed.error.issues[0]!.message);
  return write(context, section, { title: parsed.data.title, notes: parsed.data.notes });
}

/**
 * Show a section to the owner, or take it back.
 *
 * **An empty section cannot be shared.** The owner would get a heading with
 * nothing under it, which reads as a page that failed to load rather than
 * as a room nobody has specified yet.
 *
 * `firstSharedAt` is stamped once and never cleared, because it is what
 * makes unsharing and deleting different acts — see `deleteSpecSection`.
 */
export async function shareSpecSection(
  context: SpecContext,
  section: SpecSection,
  shared: boolean,
): Promise<SpecSection> {
  if (shared && isSpecSectionEmpty(section)) {
    throw new SpecError(`${section.title} has nothing in it yet, so there is nothing to show.`);
  }
  return write(context, section, {
    shared,
    ...(shared && !section.firstSharedAt
      ? { firstSharedAt: context.clock.now().toISOString() }
      : {}),
  });
}

/**
 * Remove a section, but only one the owner has never seen.
 *
 * A section they have read is part of the record of what was agreed, and
 * deleting it would destroy their copy of it. Unsharing is the way to take
 * something off their sheet; the refusal says so, because somebody who
 * wanted it gone needs to know what to do instead.
 */
export async function deleteSpecSection(context: SpecContext, section: SpecSection): Promise<void> {
  if (section.firstSharedAt) {
    throw new SpecError(
      `${section.title} has been shown to the owner, so it cannot be deleted. Unshare it instead.`,
    );
  }
  await sections(context).delete(section.id);
}

/**
 * Move a section to sit between two others.
 *
 * One write to one document, which is the whole reason positions are
 * spaced. When the gap has closed `positionBetween` says so and this
 * refuses rather than rounding the section into somewhere it was not
 * dropped — the caller renumbers the sheet, which is a visible act.
 */
export async function moveSpecSection(
  context: SpecContext,
  section: SpecSection,
  above: SpecSection | null,
  below: SpecSection | null,
): Promise<SpecSection> {
  const position = positionBetween(above?.position ?? null, below?.position ?? null);
  if (position === null) {
    throw new SpecError('There is no room between those two sections. Renumber the sheet first.');
  }
  return write(context, section, { position });
}

/**
 * Space the whole sheet out again.
 *
 * The escape hatch `moveSpecSection` points at. Every section is rewritten
 * in one commit, each guarded by the copy it was read from, so a sheet
 * somebody else is editing is refused whole rather than renumbered halfway.
 */
export async function renumberSheet(context: SpecContext): Promise<SpecSection[]> {
  const sheet = await listSpecSections(context);
  const updatedAt = context.clock.now().toISOString();
  // Counted from the index, **not** from the positions being replaced.
  // Deriving each new position from the old ones read correctly and was
  // wrong: on a sheet that had been reordered, the highest old position
  // seen so far was not the one just assigned, so two sections came out
  // on the same number — which is the one thing a renumbering exists to
  // prevent. Found by the test that renumbers a sheet after a move.
  const spaced = sheet.map((section, index) => ({
    ...section,
    position: (index + 1) * POSITION_STEP,
    updatedAt,
    updatedBy: context.uid,
  }));
  await context.data.commit(
    spaced.map((section, index) =>
      updateWrite<SpecSection>(
        scope(context),
        'specSections',
        section.id,
        { position: section.position, updatedAt, updatedBy: context.uid },
        expectUnchanged(sheet[index]!),
      ),
    ),
  );
  return spaced;
}

export const ItemInputSchema = SpecItemSchema.omit({ id: true });
export type ItemInput = z.input<typeof ItemInputSchema>;

export async function addSpecItem(
  context: SpecContext,
  section: SpecSection,
  input: ItemInput,
): Promise<SpecSection> {
  const parsed = ItemInputSchema.safeParse(input);
  if (!parsed.success) throw new SpecError(parsed.error.issues[0]!.message);
  if (section.items.length >= MAX_SPEC_ITEMS) {
    throw new SpecError(`A section holds at most ${MAX_SPEC_ITEMS} items. Add another section.`);
  }
  const item: SpecItem = { id: newId(), ...parsed.data };
  return write(context, section, { items: [...section.items, item] });
}

export async function editSpecItem(
  context: SpecContext,
  section: SpecSection,
  itemId: string,
  input: ItemInput,
): Promise<SpecSection> {
  const parsed = ItemInputSchema.safeParse(input);
  if (!parsed.success) throw new SpecError(parsed.error.issues[0]!.message);
  if (!section.items.some((one) => one.id === itemId)) {
    throw new SpecError('That item is not in this section any more.');
  }
  return write(context, section, {
    items: section.items.map((one) => (one.id === itemId ? { ...one, ...parsed.data } : one)),
  });
}

export async function removeSpecItem(
  context: SpecContext,
  section: SpecSection,
  itemId: string,
): Promise<SpecSection> {
  const remaining = section.items.filter((one) => one.id !== itemId);
  if (remaining.length === section.items.length) {
    // Refused rather than treated as done, as everywhere else in this
    // repository: a caller removing something absent has stale data, and
    // saying so is how they find out.
    throw new SpecError('That item is not in this section any more.');
  }
  return write(context, section, { items: remaining });
}

/** Move an item within its section. Array order is the item order. */
export async function moveSpecItem(
  context: SpecContext,
  section: SpecSection,
  itemId: string,
  to: number,
): Promise<SpecSection> {
  const from = section.items.findIndex((one) => one.id === itemId);
  if (from === -1) throw new SpecError('That item is not in this section any more.');
  if (to < 0 || to >= section.items.length) {
    throw new SpecError('That is not a place in this section.');
  }
  const items = [...section.items];
  const [moved] = items.splice(from, 1);
  items.splice(to, 0, moved!);
  return write(context, section, { items });
}
